import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Stethoscope, AlarmClockOff } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCfgSanitarie } from "../lib/cfgSanitarie";

const ymdRm = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const fGiorno = (s) => { const t = new Date(s + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); return t.charAt(0).toUpperCase() + t.slice(1); };

const useRiep = () => {
  const { selectedStructure } = useAuth();
  return useQuery({
    queryKey: ["vm-riepilogo", selectedStructure],
    refetchInterval: 60000,
    queryFn: async () => { try { return (await api.get("/visite-mediche/riepilogo", { params: selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {} })).data; } catch { return null; } },
  }).data;
};

const RigaVisita = ({ v, oggi, nav, rosso }) => (
  <button type="button" onClick={() => nav(`/cartella-clinica?ospite=${v.ospite_id}`)} className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-slate-50 ${rosso ? "border-red-200 bg-red-50/40" : v.evento ? "border-rose-200" : ""}`} data-testid={`dash-visita-${rosso ? "saltata" : "sett"}-${v.id}`}>
    <div className="flex flex-wrap items-baseline gap-x-2">
      <b className="truncate">{v.nome}</b>
      <span className="ml-auto text-[12px] font-semibold text-slate-700"><span className={v.data === oggi ? "text-emerald-700" : rosso ? "text-red-700" : ""}>{v.data === oggi ? "Oggi" : fGiorno(v.data)}</span>{v.ora ? ` · ore ${v.ora}` : ""} · {v.medico || "medico non indicato"}</span>
    </div>
    <div className={`text-[11px] font-bold uppercase tracking-wide ${v.evento ? "text-rose-700" : "text-teal-700"}`}>{v.evento ? "Visita per evento sanitario" : "Visita ordinaria"}{rosso && <span className="ml-1 text-red-700">· esito da inserire</span>}</div>
  </button>
);

export const VisiteSettimanaBox = () => {
  const nav = useNavigate();
  const cfg = useCfgSanitarie();
  const data = useRiep();
  if (!data) return null;
  const n = cfg.giorni_dash_visite;
  const oggi = ymdRm(new Date());
  const fine = ymdRm(new Date(Date.now() + (n - 1) * 86400000));
  const list = (data.visite || []).filter((v) => v.data >= oggi && v.data <= fine);
  return (
    <div className="gr-card p-5" data-testid="dash-visite-settimana">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head font-bold flex items-center gap-2"><Stethoscope size={18} className="text-teal-600" /> Visite mediche dei prossimi {n} giorni</h2>
        <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-teal-600 text-white tabnum" data-testid="dash-visite-settimana-count">{list.length}</span>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna visita programmata nei prossimi {n} giorni.</p> : (
        <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">{list.map((v) => <RigaVisita key={v.id} v={v} oggi={oggi} nav={nav} />)}</div>)}
    </div>
  );
};

export const VisiteSaltateBox = () => {
  const nav = useNavigate();
  const data = useRiep();
  const list = data?.saltate || [];
  if (!list.length) return null;
  return (
    <div className="gr-card p-5 border-2 border-red-300" data-testid="dash-visite-saltate">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-head font-bold flex items-center gap-2 text-red-800"><AlarmClockOff size={18} /> Visite saltate: esito da inserire</h2>
        <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-red-600 text-white tabnum" data-testid="dash-visite-saltate-count">{list.length}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-2">Visite con data passata ancora senza esito: apri la visita e inserisci cosa ha detto il medico (o se è stata rimandata/annullata).</p>
      <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">{list.map((v) => <RigaVisita key={v.id} v={v} nav={nav} rosso />)}</div>
    </div>
  );
};
